import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { accountingGateway } from '../../accounting';
import { ALL_GL_CATEGORIES, glCategoryLabels } from '../../accounting/types';
import type { GlExportResponse, GlMappingResponse, PayrollRunSummary } from '../../accounting/types';
import { formatReportCurrency, formatReportDate } from '../../reports/formatting';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { downloadCsv } from '../csvExport';
import '../accounting-reports.css';

function runLabel(run: PayrollRunSummary): string {
  return `${formatReportDate(run.periodStartDate)} – ${formatReportDate(run.periodEndDate)} · Check date ${formatReportDate(run.checkDate)}`;
}

type ViewMode = 'basic' | 'detailed';

/** EPN-77 General Ledger report. Reads the SAME `GlExportResponse` history EPN.Accounting.Api's existing
 * "GL Exports & Reconciliation" admin screen (`GlExportsPage`) already exposes via `accountingGateway` —
 * this page only presents it as a report (Basic/Detailed, mapping status, balance banner); it never
 * triggers a new export itself ("Reports are read-only for underlying payroll values"). Basic vs. Detailed
 * is a client-side presentation choice: the backend has no separate aggregation for either — both render
 * the same `lines`, Basic collapsing the Description column and Detailed showing it in full. */
export function GeneralLedgerReportPage() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const [mappings, setMappings] = useState<GlMappingResponse[]>([]);
  const [exportsForRun, setExportsForRun] = useState<GlExportResponse[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>('detailed');

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .listApprovedPayrollRuns(controller.signal)
      .then((result) => {
        const sorted = [...result].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
        setRuns(sorted);
        setSelectedRunId(sorted[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRunsError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRunsLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDetailLoading(true);
    setDetailError(null);
    setExportsForRun([]);
    Promise.all([accountingGateway.getGlMappings(controller.signal), accountingGateway.listGlExports(selectedRunId, controller.signal)])
      .then(([mappingRows, exportRows]) => {
        if (controller.signal.aborted) return;
        setMappings(mappingRows);
        setExportsForRun(exportRows);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setDetailError(friendlyErrorMessage(err, 'Could not load the general ledger for this run.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [selectedRunId]);

  const mappedCategories = useMemo(() => new Set(mappings.map((m) => m.category)), [mappings]);
  const latestExport = exportsForRun[0] ?? null;
  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;
  const balanced = latestExport ? latestExport.totalDebits === latestExport.totalCredits : null;

  function exportCsv() {
    if (!latestExport) return;
    downloadCsv(
      `general-ledger-${latestExport.payrollRunId}.csv`,
      ['Posting Date', 'Payroll Run', 'Account Code', 'Account Name', 'Description', 'Debit', 'Credit', 'Mapping Status'],
      latestExport.lines.map((line) => [
        latestExport.exportedAtUtc.slice(0, 10),
        latestExport.checkDate,
        line.glAccountCode,
        glCategoryLabels[line.category] ?? line.glAccountName,
        line.description,
        line.isDebit ? line.amount.toFixed(2) : '',
        !line.isDebit ? line.amount.toFixed(2) : '',
        mappedCategories.has(line.category) ? 'Mapped' : 'Missing',
      ]),
    );
  }

  const chips = [
    selectedRun ? { key: 'run', label: `Payroll run: ${formatReportDate(selectedRun.checkDate)}`, onRemove: () => setSelectedRunId(runs[0]?.id ?? null) } : null,
    { key: 'view', label: view === 'basic' ? 'View: Basic' : 'View: Detailed', onRemove: () => setView('detailed') },
  ].filter((c): c is NonNullable<typeof c> => c !== null && !(c.key === 'view' && view === 'detailed'));

  return (
    <section className="card card--wide" aria-labelledby="general-ledger-heading">
      <div className="section-header">
        <h2 id="general-ledger-heading">General Ledger</h2>
      </div>

      {runsLoading && <p className="muted" role="status">Loading approved payroll runs…</p>}
      {runsError && <p className="error" role="alert">{runsError}</p>}
      {!runsLoading && !runsError && runs.length === 0 && (
        <p className="report-empty-state">No approved payroll runs yet. A General Ledger entry becomes available once a run is approved and exported.</p>
      )}

      {!runsLoading && runs.length > 0 && (
        <div className="report-filter-bar">
          <div className="report-filter-field">
            <label htmlFor="gl-run">Payroll run</label>
            <select id="gl-run" value={selectedRunId ?? ''} onChange={(e) => setSelectedRunId(e.target.value)}>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>{runLabel(r)}</option>
              ))}
            </select>
          </div>
          <div className="report-filter-actions">
            <div className="tabs" role="tablist" aria-label="Basic or Detailed view">
              <button type="button" role="tab" aria-selected={view === 'basic'} className={view === 'basic' ? 'tab tab--active' : 'tab'} onClick={() => setView('basic')}>Basic</button>
              <button type="button" role="tab" aria-selected={view === 'detailed'} className={view === 'detailed' ? 'tab tab--active' : 'tab'} onClick={() => setView('detailed')}>Detailed</button>
            </div>
            <button type="button" onClick={exportCsv} disabled={!latestExport}>Export CSV</button>
          </div>
        </div>
      )}

      <ActiveFilterChips chips={chips} onClearAll={() => setView('detailed')} />

      {detailLoading && <p className="muted" role="status">Loading general ledger…</p>}
      {detailError && <p className="error" role="alert">{detailError}</p>}

      {!detailLoading && !detailError && selectedRunId && !latestExport && (
        <p className="report-empty-state">
          This payroll run has not been exported to the general ledger yet.{' '}
          <Link to="/accounting">Export it from Accounting &amp; Reconciliation</Link> to see it here.
        </p>
      )}

      {latestExport && (
        <>
          <p className={`report-balance-banner ${balanced ? 'report-balance-banner--balanced' : 'report-balance-banner--out-of-balance'}`}>
            {balanced ? 'Balanced' : 'Out of balance'} — Total Debits {formatReportCurrency(latestExport.totalDebits)}, Total Credits{' '}
            {formatReportCurrency(latestExport.totalCredits)}, Difference {formatReportCurrency(Math.abs(latestExport.totalDebits - latestExport.totalCredits))}
          </p>

          <table aria-label="General ledger lines">
            <thead>
              <tr>
                <th scope="col">Posting Date</th>
                <th scope="col">Payroll Run</th>
                <th scope="col">Account Code</th>
                <th scope="col">Account Name</th>
                {view === 'detailed' && <th scope="col">Description</th>}
                <th scope="col">Debit</th>
                <th scope="col">Credit</th>
                <th scope="col">Mapping Status</th>
                <th scope="col">Sync Status</th>
              </tr>
            </thead>
            <tbody>
              {latestExport.lines.map((line) => (
                <tr key={line.category}>
                  <td>{formatReportDate(latestExport.exportedAtUtc.slice(0, 10))}</td>
                  <td>{formatReportDate(latestExport.checkDate)}</td>
                  <td>{line.glAccountCode}</td>
                  <th scope="row">{glCategoryLabels[line.category] ?? line.glAccountName}</th>
                  {view === 'detailed' && <td>{line.description}</td>}
                  <td>{line.isDebit ? formatReportCurrency(line.amount) : ''}</td>
                  <td>{!line.isDebit ? formatReportCurrency(line.amount) : ''}</td>
                  <td>
                    {mappedCategories.has(line.category) ? (
                      <span className="badge badge--success">Mapped</span>
                    ) : (
                      <span className="badge badge--warning">Missing</span>
                    )}
                  </td>
                  <td><span className="badge badge--success">Synced (Mock Provider)</span></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={view === 'detailed' ? 5 : 4}>Totals</th>
                <td>{formatReportCurrency(latestExport.totalDebits)}</td>
                <td>{formatReportCurrency(latestExport.totalCredits)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>

          {mappedCategories.size < ALL_GL_CATEGORIES.length && (
            <p className="muted">
              {ALL_GL_CATEGORIES.length - mappedCategories.size} of {ALL_GL_CATEGORIES.length} GL categories still need a mapping.{' '}
              <Link to="/accounting">Review Accounting Mapping</Link>
            </p>
          )}
        </>
      )}
    </section>
  );
}
